import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { Users, Gift, Check, X, Clock, TrendingUp, CreditCard, Bell, ChevronDown, Eye, Coins, Ticket, Twitter, Linkedin, Facebook, Mail, Share2, Link2 } from "lucide-react";
import ImpersonationAuditWidget from "./ImpersonationAuditWidget";

const CREDIT_AMOUNTS = [20, 50, 100, 200, 300, 500];

// Small WhatsApp glyph — lucide doesn't ship one
function WhatsAppIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function shareMessage(code, amount) {
  const appUrl = (typeof window !== "undefined" && window.location ? window.location.origin : "https://toprankpulse.com");
  const url = `${appUrl}/redeem/${code}`;
  const text = `🎁 Free ${amount} Ads Creator credits on TopRankPulse! Redeem code ${code} here:`;
  return { url, text, full: `${text} ${url}` };
}

function CouponShareButtons({ code, amount }) {
  const { url, text, full } = shareMessage(code, amount);
  const enc = encodeURIComponent;
  const links = [
    { label: "Twitter", icon: Twitter, color: "#1da1f2",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { label: "LinkedIn", icon: Linkedin, color: "#0a66c2",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { label: "WhatsApp", icon: WhatsAppIcon, color: "#25d366",
      href: `https://api.whatsapp.com/send?text=${enc(full)}` },
    { label: "Facebook", icon: Facebook, color: "#1877f2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}` },
    { label: "Email", icon: Mail, color: "#475569",
      href: `mailto:?subject=${enc("Free TopRankPulse ads credits")}&body=${enc(full)}` },
  ];
  return (
    <div className="flex items-center gap-1" data-testid={`coupon-share-${code}`}>
      {links.map(({ label, icon: Icon, color, href }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer"
          title={`Share via ${label}`}
          data-testid={`coupon-share-${code}-${label.toLowerCase()}`}
          className="w-6 h-6 inline-flex items-center justify-center rounded hover:opacity-80 transition"
          style={{ background: color, color: "#fff" }}>
          <Icon className="w-3.5 h-3.5" />
        </a>
      ))}
      <button
        title="Copy redeem link"
        data-testid={`coupon-share-${code}-copylink`}
        onClick={() => { navigator.clipboard.writeText(url); }}
        className="w-6 h-6 inline-flex items-center justify-center rounded bg-gray-700 hover:bg-gray-900 text-white">
        <Link2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

const SERVICES = [
  { id: "brand-monitor", label: "AI Brand Monitor", price: "€19/mes" },
  { id: "competitor-spy", label: "Konkurenčný špión", price: "€29/mes" },
  { id: "seo-autopilot", label: "SEO Autopilot", price: "€79/mes" },
  { id: "blog-agent", label: "Blog Agent", price: "€25" },
  { id: "fix-my-seo", label: "Oprav moje SEO", price: "€9" },
  { id: "attribution", label: "AI Atribúcia", price: "€39/mes" },
  { id: "ads-creator", label: "Ads Creator", price: "€69/mes" },
];

function GrantModal({ user, onClose, onGrant }) {
  const [service, setService] = useState(SERVICES[0].id);
  const [days, setDays] = useState(14);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onGrant(user.user_id, service, days, note);
    setSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="grant-modal">
        <h3 className="font-bold text-lg mb-1">Pridať službu zadarmo</h3>
        <p className="text-sm text-gray-500 mb-4">{user.email}</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Služba</label>
            <select value={service} onChange={e => setService(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 text-sm rounded" data-testid="grant-service-select">
              {SERVICES.map(s => <option key={s.id} value={s.id}>{s.label} ({s.price})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Počet dní</label>
            <div className="flex gap-2">
              {[7, 14, 30, 90].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`px-3 py-1.5 text-sm font-medium rounded ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  {d} dní
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Poznámka (voliteľné)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Napr. referral odmena"
              className="w-full px-3 py-2 border border-gray-300 text-sm rounded" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded hover:bg-gray-50">Zrušiť</button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded hover:bg-emerald-700 disabled:opacity-50" data-testid="grant-submit-btn">
            {submitting ? "..." : `Pridať ${days} dní`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreditsGrantModal({ user, onClose }) {
  const [amount, setAmount] = useState(50);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/ads-creator/credits/admin-grant`,
        { user_id: user.user_id, amount, note },
        { withCredentials: true });
      onClose();
    } catch (e) {
      alert(`Chyba: ${e?.response?.data?.detail || e.message}`);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="credits-grant-modal">
        <h3 className="font-bold text-lg mb-1 flex items-center gap-2"><Coins className="w-5 h-5 text-amber-500" /> Pridať kredity</h3>
        <p className="text-sm text-gray-500 mb-4">{user.email}</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Počet kreditov</label>
            <div className="grid grid-cols-3 gap-2">
              {CREDIT_AMOUNTS.map(a => (
                <button key={a} onClick={() => setAmount(a)}
                  data-testid={`credits-amount-${a}`}
                  className={`px-3 py-2 text-sm font-bold rounded ${amount === a ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                  +{a}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Poznámka (voliteľné)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Napr. kompenzácia, referral bonus"
              data-testid="credits-grant-note"
              className="w-full px-3 py-2 border border-gray-300 text-sm rounded" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded hover:bg-gray-50">Zrušiť</button>
          <button onClick={handleSubmit} disabled={submitting}
            data-testid="credits-grant-submit"
            className="flex-1 px-4 py-2 bg-amber-500 text-white text-sm font-bold rounded hover:bg-amber-600 disabled:opacity-50">
            {submitting ? "..." : `Pridať +${amount} kreditov`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CouponGeneratorModal({ onClose }) {
  const [amount, setAmount] = useState(50);
  const [count, setCount] = useState(1);
  const [expiresDays, setExpiresDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  const fetchCoupons = useCallback(async () => {
    setListLoading(true);
    try {
      const r = await axios.get(`${API}/ads-creator/credits/coupons/list`, { withCredentials: true });
      setCoupons(r.data.coupons || []);
      setStats(r.data.stats || null);
    } catch (e) { console.error(e); }
    setListLoading(false);
  }, []);
  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ads-creator/credits/coupons/generate`,
        { amount, count, expires_days: expiresDays, note },
        { withCredentials: true });
      setGenerated(r.data.coupons || []);
      fetchCoupons();
    } catch (e) {
      alert(`Chyba: ${e?.response?.data?.detail || e.message}`);
    }
    setBusy(false);
  };

  const deactivate = async (code) => {
    if (!window.confirm(`Deaktivovať kupón ${code}?`)) return;
    try {
      await axios.post(`${API}/ads-creator/credits/coupons/deactivate`, { code }, { withCredentials: true });
      fetchCoupons();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="coupon-generator-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg flex items-center gap-2"><Ticket className="w-5 h-5 text-pink-500" /> Kupóny na kredity</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-gray-50 p-3 rounded"><p className="text-xs text-gray-500">Celkom</p><p className="text-xl font-mono font-bold">{stats.total}</p></div>
            <div className="bg-emerald-50 p-3 rounded"><p className="text-xs text-emerald-600">Použité</p><p className="text-xl font-mono font-bold text-emerald-700">{stats.redeemed}</p></div>
            <div className="bg-blue-50 p-3 rounded"><p className="text-xs text-blue-600">Nepoužité / aktívne</p><p className="text-xl font-mono font-bold text-blue-700">{stats.active_unused}</p></div>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gradient-to-br from-pink-50 to-purple-50">
          <h4 className="font-bold text-sm mb-3">Generovať nové kupóny</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Hodnota (kreditov)</label>
              <div className="grid grid-cols-6 gap-2">
                {CREDIT_AMOUNTS.map(a => (
                  <button key={a} onClick={() => setAmount(a)}
                    data-testid={`coupon-amount-${a}`}
                    className={`px-2 py-2 text-sm font-bold rounded ${amount === a ? 'bg-pink-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-pink-50'}`}>
                    +{a}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Počet kódov</label>
                <input type="number" value={count} min={1} max={50}
                  onChange={e => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  data-testid="coupon-count-input"
                  className="w-full px-3 py-2 border border-gray-300 text-sm rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Platnosť (dní)</label>
                <input type="number" value={expiresDays} min={1} max={365}
                  onChange={e => setExpiresDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 30)))}
                  className="w-full px-3 py-2 border border-gray-300 text-sm rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Poznámka</label>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="napr. Q2 launch"
                  className="w-full px-3 py-2 border border-gray-300 text-sm rounded" />
              </div>
            </div>
            <button onClick={generate} disabled={busy}
              data-testid="coupon-generate-btn"
              className="w-full py-2.5 bg-pink-600 text-white text-sm font-bold rounded hover:bg-pink-700 disabled:opacity-50">
              {busy ? "Generujem..." : `✨ Generuj ${count}× kupón (+${amount} kreditov)`}
            </button>
          </div>

          {generated.length > 0 && (
            <div className="mt-3 p-3 bg-white border border-emerald-300 rounded" data-testid="coupon-generated-list">
              <p className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Vygenerované ({generated.length}) — zdieľaj rovno!
              </p>
              <div className="space-y-2">
                {generated.map(c => (
                  <div key={c.code} className="flex items-center justify-between gap-2 py-1.5 px-2 bg-gray-50 rounded">
                    <span className="font-mono text-sm font-bold">{c.code}</span>
                    <div className="flex items-center gap-2">
                      <CouponShareButtons code={c.code} amount={c.amount} />
                      <button onClick={() => copyCode(c.code)} className="text-xs px-2 py-0.5 bg-gray-200 hover:bg-gray-300 rounded">Copy</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
                <Share2 className="w-3 h-3" /> Každé zdieľanie = potenciálny nový používateľ.
              </p>
            </div>
          )}
        </div>

        <div>
          <h4 className="font-bold text-sm mb-2">Všetky kupóny</h4>
          {listLoading ? (
            <p className="text-center text-gray-400 text-sm py-4">Načítavam...</p>
          ) : coupons.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-4">Žiadne kupóny</p>
          ) : (
            <div className="border border-gray-200 rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="coupons-table">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                  <tr>
                    <th className="px-3 py-2 text-left">Kód</th>
                    <th className="px-3 py-2 text-center">Hodnota</th>
                    <th className="px-3 py-2 text-left">Stav</th>
                    <th className="px-3 py-2 text-left">Použil</th>
                    <th className="px-3 py-2 text-left">Expirácia</th>
                    <th className="px-3 py-2 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {coupons.map(c => (
                    <tr key={c.code} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono">{c.code}</td>
                      <td className="px-3 py-2 text-center font-bold text-amber-600">+{c.amount}</td>
                      <td className="px-3 py-2">
                        {c.redeemed_by ? <span className="text-emerald-600 font-bold">Použitý</span>
                          : !c.is_active ? <span className="text-gray-400">Deaktivovaný</span>
                          : <span className="text-blue-600">Aktívny</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-500 truncate max-w-[150px]">{c.redeemed_by || "—"}</td>
                      <td className="px-3 py-2 text-gray-500">{c.expires_at?.slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        {c.is_active && !c.redeemed_by && (
                          <div className="flex items-center justify-center gap-2">
                            <CouponShareButtons code={c.code} amount={c.amount} />
                            <button onClick={() => copyCode(c.code)} className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded">Copy</button>
                            <button onClick={() => deactivate(c.code)}
                              data-testid={`coupon-deactivate-${c.code}`}
                              className="text-red-400 hover:text-red-600" title="Deaktivovať"><X className="w-3 h-3" /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniBar({ data, maxVal }) {
  return (
    <div className="flex items-end gap-0.5 h-10">
      {data.map((v, i) => (
        <div key={i} className="w-1.5 bg-blue-500 rounded-t" style={{ height: `${Math.max(2, (v / (maxVal || 1)) * 100)}%` }} />
      ))}
    </div>
  );
}

export default function AdminUsersTab() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [grantUser, setGrantUser] = useState(null);
  const [creditsUser, setCreditsUser] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin-users/list`, { withCredentials: true }),
        axios.get(`${API}/admin-users/stats`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data.users);
      setTotal(usersRes.data.total);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGrant = async (userId, service, days, note) => {
    try {
      await axios.post(`${API}/admin-users/grant-service`, { user_id: userId, service, days, note }, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleRevoke = async (userId, service) => {
    try {
      await axios.delete(`${API}/admin-users/revoke-service?user_id=${userId}&service=${service}`, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Načítavam...</div>;

  // Build chart data from last 30 days
  const regData = [];
  if (stats?.daily_registrations) {
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      regData.push(stats.daily_registrations[key] || 0);
    }
  }
  const maxReg = Math.max(...regData, 1);

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Celkom používateľov</p>
          <p className="text-3xl font-mono font-bold text-gray-900">{stats?.total_users || 0}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Platby dokončené</p>
          <p className="text-3xl font-mono font-bold text-emerald-600">{stats?.completed_payments || 0}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium">Push odberatelia</p>
          <p className="text-3xl font-mono font-bold text-blue-600">{stats?.push_subscribers || 0}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4 rounded-lg col-span-2">
          <p className="text-xs text-gray-500 uppercase font-medium mb-2">Registrácie (30 dní)</p>
          <MiniBar data={regData} maxVal={maxReg} />
        </div>
      </div>

      {/* Active services distribution */}
      {stats?.active_services && Object.keys(stats.active_services).length > 0 && (
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-xs text-gray-500 uppercase font-medium mb-3">Aktívne služby</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.active_services).map(([svc, count]) => (
              <span key={svc} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded">
                {SERVICES.find(s => s.id === svc)?.label || svc}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Impersonation audit */}
      <ImpersonationAuditWidget />

      {/* Users table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><Users className="w-4 h-4" /> Zoznam používateľov ({total})</h3>
          <button onClick={() => setShowCouponModal(true)}
            data-testid="open-coupon-generator"
            className="px-3 py-1.5 bg-pink-600 text-white text-xs font-bold rounded hover:bg-pink-700 flex items-center gap-1.5">
            <Ticket className="w-3.5 h-3.5" /> Kupóny
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2.5 text-left">Email</th>
                <th className="px-4 py-2.5 text-left">Meno</th>
                <th className="px-4 py-2.5 text-left">Rola</th>
                <th className="px-4 py-2.5 text-left">Registrácia</th>
                <th className="px-4 py-2.5 text-left">Posledný</th>
                <th className="px-4 py-2.5 text-center">Platby</th>
                <th className="px-4 py-2.5 text-center">Služby</th>
                <th className="px-4 py-2.5 text-center">Akcie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u, i) => {
                const isExpanded = expandedUser === u.user_id;
                const activeCount = (u.active_services || []).filter(s => new Date(s.expires_at) > new Date()).length;
                return (
                  <>
                    <tr key={u.user_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedUser(isExpanded ? null : u.user_id)} data-testid={`user-row-${i}`}>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{u.email}</td>
                      <td className="px-4 py-3 text-gray-600">{u.name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 font-bold ${u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                          {u.role || "user"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{u.created_at?.slice(0, 10) || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{u.last_seen?.slice(0, 10) || "—"}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-bold ${u.payment_count > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>{u.payment_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {activeCount > 0 ? (
                          <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 font-bold">{activeCount} aktívne</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center flex-wrap">
                          <button onClick={e => { e.stopPropagation(); setGrantUser(u); }}
                            className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 rounded" data-testid={`grant-btn-${i}`}>
                            <Gift className="w-3 h-3 inline mr-1" />Služba
                          </button>
                          <button onClick={e => { e.stopPropagation(); setCreditsUser(u); }}
                            className="px-2 py-1 bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 rounded" data-testid={`credits-btn-${i}`}>
                            <Coins className="w-3 h-3 inline mr-1" />Kredity
                          </button>
                          {u.role !== "admin" && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Prepnúť sa do účtu ${u.email}? Všetky akcie budú logované.`)) return;
                                try {
                                  const r = await axios.post(
                                    `${API}/admin-users/impersonate/${u.user_id}`,
                                    {},
                                    { withCredentials: true },
                                  );
                                  // Store admin's current token for later restore
                                  const adminToken = localStorage.getItem("access_token");
                                  if (adminToken) {
                                    localStorage.setItem("admin_restore_token", adminToken);
                                  }
                                  localStorage.setItem("access_token", r.data.access_token);
                                  localStorage.setItem("impersonation_target", u.email);
                                  window.location.href = "/dashboard";
                                } catch (err) {
                                  alert(`Impersonate failed: ${err?.response?.data?.detail || err.message}`);
                                }
                              }}
                              className="px-2 py-1 bg-amber-50 text-amber-700 text-xs font-bold hover:bg-amber-100 rounded"
                              data-testid={`impersonate-btn-${i}`}
                              title="View as this user (audit logged)"
                            >
                              <Eye className="w-3 h-3 inline mr-1" />View as
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${u.user_id}-detail`}>
                        <td colSpan={8} className="px-4 py-3 bg-gray-50">
                          <div className="space-y-2">
                            <p className="text-xs text-gray-500">
                              <strong>Firma:</strong> {u.company || "—"} | <strong>Web:</strong> {u.website || "—"} | <strong>IČO:</strong> {u.billing_ico || "—"} | <strong>DIČ:</strong> {u.billing_dic || "—"}
                            </p>
                            {(u.active_services || []).length > 0 ? (
                              <div className="space-y-1">
                                <p className="text-xs font-bold text-gray-700">Aktívne služby:</p>
                                {u.active_services.map((s, si) => {
                                  const expired = new Date(s.expires_at) < new Date();
                                  return (
                                    <div key={si} className="flex items-center justify-between py-1 px-2 bg-white border border-gray-100 rounded text-xs">
                                      <span className="font-medium">{SERVICES.find(sv => sv.id === s.service)?.label || s.service}</span>
                                      <div className="flex items-center gap-2">
                                        <span className={expired ? "text-red-500" : "text-emerald-600"}>
                                          do {s.expires_at?.slice(0, 10)}
                                        </span>
                                        {s.admin_note && <span className="text-gray-400">({s.admin_note})</span>}
                                        <button onClick={() => handleRevoke(u.user_id, s.service)}
                                          className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400">Žiadne aktívne služby</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {grantUser && <GrantModal user={grantUser} onClose={() => setGrantUser(null)} onGrant={handleGrant} />}
      {creditsUser && <CreditsGrantModal user={creditsUser} onClose={() => setCreditsUser(null)} />}
      {showCouponModal && <CouponGeneratorModal onClose={() => setShowCouponModal(false)} />}
    </div>
  );
}
